import {
  MARKETING_PUBLIC_PREFIXES,
  MARKETING_RESOURCE_PAGES,
} from "@/lib/marketing/marketingNav";

export const MARKETING_LOCALES = ["en", "ar", "zh", "zh-tw", "es", "de", "fr", "pt", "hi", "ko", "it", "ru", "vi", "ja", "nl", "pl", "id", "ro"] as const;
export type MarketingLocale = (typeof MARKETING_LOCALES)[number];

export const MARKETING_LOCALE_LABELS: Record<MarketingLocale, string> = {
  en: "English",
  ar: "العربية",
  zh: "简体中文",
  "zh-tw": "繁體中文",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  pt: "Português",
  hi: "हिन्दी",
  ko: "한국어",
  it: "Italiano",
  ru: "Русский",
  vi: "Tiếng Việt",
  ja: "日本語",
  nl: "Nederlands",
  pl: "Polski",
  id: "Bahasa Indonesia",
  ro: "Română",
};

export const MARKETING_LANGUAGE_LABELS: Record<MarketingLocale, string> = {
  en: "Language",
  ar: "اللغة",
  zh: "语言",
  "zh-tw": "語言",
  es: "Idioma",
  de: "Sprache",
  fr: "Langue",
  pt: "Idioma",
  hi: "भाषा",
  ko: "언어",
  it: "Lingua",
  ru: "Язык",
  vi: "Ngôn ngữ",
  ja: "言語",
  nl: "Taal",
  pl: "Język",
  id: "Bahasa",
  ro: "Limbă",
};

export const MARKETING_OPEN_GRAPH_LOCALES: Record<MarketingLocale, string> = {
  en: "en_US",
  ar: "ar_AR",
  zh: "zh_CN",
  "zh-tw": "zh_TW",
  es: "es_ES",
  de: "de_DE",
  fr: "fr_FR",
  pt: "pt_PT",
  hi: "hi_IN",
  ko: "ko_KR",
  it: "it_IT",
  ru: "ru_RU",
  vi: "vi_VN",
  ja: "ja_JP",
  nl: "nl_NL",
  pl: "pl_PL",
  id: "id_ID",
  ro: "ro_RO",
};

const PREFIXED_MARKETING_LOCALES = MARKETING_LOCALES.filter(
  (locale): locale is Exclude<MarketingLocale, "en"> => locale !== "en",
).sort((a, b) => b.length - a.length);

export const MARKETING_LOCALE_COOKIE = "nc_marketing_locale";
export const MARKETING_LOCALE_DISMISSAL_KEY = "novilot-marketing-locale-suggestion-v1";

const MARKETING_EXACT_PATHS = new Set([
  "/",
  "/ai",
  "/faq",
  "/pricing",
  "/privacy",
  "/terms",
  "/sign-in",
  "/sign-up",
  ...MARKETING_RESOURCE_PAGES.map((page) => page.href),
]);

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const path = pathname.split(/[?#]/u, 1)[0] || "/";
  if (path === "/") return "/";
  const withoutTrailingSlash = path.replace(/\/+$/u, "");
  return withoutTrailingSlash || "/";
}

export function normalizeMarketingLocale(value: unknown): MarketingLocale | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  const base = normalized.split("-", 1)[0];
  if (
    base === "ar" || base === "en" || base === "es" || base === "de" || base === "fr"
    || base === "pt" || base === "hi" || base === "ko" || base === "it" || base === "ru" || base === "vi"
    || base === "ja" || base === "nl" || base === "pl" || base === "id" || base === "ro"
  ) return base;
  // Handle Chinese locales: Traditional tags → zh-tw, Hans/zh-cn/zh → zh
  if (base === "zh") {
    // Traditional Chinese tags (zh-tw, zh-tw-*, zh-hant, zh-hk, zh-mo, tags with hant/tw/hk/mo)
    if (
      normalized === "zh-tw" || normalized.startsWith("zh-tw-") ||
      normalized === "zh-hant" || normalized.startsWith("zh-hant-") ||
      normalized === "zh-hk" || normalized.startsWith("zh-hk-") ||
      normalized === "zh-mo" || normalized.startsWith("zh-mo-") ||
      /(?:^|-)hant(?:-|$)/u.test(normalized) ||
      /(?:^|-)tw(?:-|$)/u.test(normalized) ||
      /(?:^|-)hk(?:-|$)/u.test(normalized) ||
      /(?:^|-)mo(?:-|$)/u.test(normalized)
    ) {
      return "zh-tw";
    }
    // Simplified Chinese tags (zh-cn, zh-hans, plain zh)
    return "zh";
  }
  return null;
}

function localePrefix(locale: MarketingLocale): string {
  return locale === "en" ? "" : `/${locale}`;
}

function prefixedLocaleFromPath(path: string): MarketingLocale | null {
  for (const locale of PREFIXED_MARKETING_LOCALES) {
    const prefix = `/${locale}`;
    if (path === prefix || path.startsWith(`${prefix}/`)) return locale;
  }
  return null;
}

/** Returns the locale encoded by a marketing URL, or null for app/asset URLs. */
export function marketingLocaleFromPath(pathname: string): MarketingLocale | null {
  const path = normalizePathname(pathname);
  const prefixedLocale = prefixedLocaleFromPath(path);
  if (prefixedLocale) {
    const unprefixed = path.slice(prefixedLocale.length + 1) || "/";
    return isMarketingPath(unprefixed) ? prefixedLocale : null;
  }
  return isMarketingPath(path) ? "en" : null;
}

/** True for the public marketing route registry, including generated tools. */
export function isMarketingPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (MARKETING_EXACT_PATHS.has(path)) return true;
  return MARKETING_PUBLIC_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function unprefixedMarketingPath(pathname: string): string {
  const path = normalizePathname(pathname);
  const prefixedLocale = prefixedLocaleFromPath(path);
  if (!prefixedLocale) return path;
  return path.slice(prefixedLocale.length + 1) || "/";
}

/** Convert an equivalent marketing URL without changing its query string. */
export function localizedMarketingPath(
  pathname: string,
  targetLocale: MarketingLocale,
): string {
  const match = pathname.match(/^([^?#]*)([?#][\s\S]*)?$/u);
  const rawPath = match?.[1] || "/";
  const suffix = match?.[2] ?? "";
  const path = unprefixedMarketingPath(rawPath);
  if (!isMarketingPath(path)) return pathname;
  const prefix = localePrefix(targetLocale);
  if (!prefix) return `${path}${suffix}`;
  return `${path === "/" ? `${prefix}/` : `${prefix}${path}`}${suffix}`;
}

/** Parse browser language preferences without depending on navigator. */
export function preferredMarketingLocale(
  values: readonly string[] | null | undefined,
): MarketingLocale {
  for (const value of values ?? []) {
    const locale = normalizeMarketingLocale(value);
    if (locale) return locale;
  }
  return "en";
}

export function browserLanguageValues(): string[] {
  if (typeof navigator === "undefined") return [];
  return Array.from(new Set([...(navigator.languages ?? []), navigator.language])).filter(Boolean);
}
